import React, { Component } from 'react';
import { getAuthHeaders, urlMap } from '../../utils';
import { AppContext } from '../../context/AppContext';
import InstagramConnectModal from './socialMedia/InstagramConnectModal';
import { fetchSubscriptionStatus, openSubscriptionTabInWallet } from '../../utils/subscriptionAccess';

/**
 * Social Media onboarding chat (MVP).
 *
 * Guides the writer to connect Instagram. When a valid handle is provided in
 * chat, the profile is persisted and the Instagram connect modal opens to run
 * the connection + integration. After connecting, the writer is pointed at the
 * next MVP step (uploading a reel).
 */

const WELCOME_TEXT =
  'Welcome to Social Media. Let\u2019s connect your Instagram so you can turn reels into scheduled posts.';
const ASK_HANDLE_TEXT = 'What\u2019s your Instagram handle? (e.g. @yourname or your profile link)';

function extractInstagramHandle(raw) {
  if (!raw) return null;
  let value = String(raw).trim();
  if (/^(no|nope|skip|none|later)\b/i.test(value)) return null;

  const urlMatch = value.match(/instagram\.com\/([^/?#\s]+)/i);
  if (urlMatch) value = urlMatch[1];

  value = value.replace(/^@+/, '').trim();
  if (!/^[a-zA-Z0-9._]{1,30}$/.test(value)) return null;
  return value;
}

class SocialMediaPage extends Component {
  static contextType = AppContext;

  constructor(props) {
    super(props);
    this.state = {
      messages: [
        { id: 'welcome', from: 'them', text: WELCOME_TEXT },
        { id: 'ask-handle', from: 'them', text: ASK_HANDLE_TEXT },
      ],
      input: '',
      handle: '',
      connected: false,
      loading: true,
      saving: false,
      modalOpen: false,
      isPro: false,
      proLoading: true,
    };
    this.messagesEndRef = React.createRef();
  }

  get projectId() {
    return this.context?.state?.currentProject?.id || null;
  }

  componentDidMount() {
    this.initSubscriptionAndData();
  }

  initSubscriptionAndData = async () => {
    const status = await fetchSubscriptionStatus();
    const isPro = !!status.isPro;
    this.setState({ isPro, proLoading: false, loading: isPro });
    if (isPro) this.loadExistingState();
  };

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.messages !== this.state.messages) {
      this.scrollToBottom();
    }
  }

  scrollToBottom = () => {
    this.messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  pushMessage = (from, text) => {
    this.setState((prev) => ({
      messages: [...prev.messages, { id: `${from}-${Date.now()}-${prev.messages.length}`, from, text }],
    }));
  };

  loadExistingState = async () => {
    const qs = this.projectId ? `?projectId=${encodeURIComponent(this.projectId)}` : '';
    try {
      const [profileRes, connectionsRes] = await Promise.all([
        fetch(`${urlMap.socialProfile}${qs}`, {
          headers: { ...getAuthHeaders() },
          credentials: 'include',
        }),
        fetch(`${urlMap.socialConnections}${qs}`, {
          headers: { ...getAuthHeaders() },
          credentials: 'include',
        }),
      ]);

      const profileData = await profileRes.json().catch(() => ({}));
      const connectionsData = await connectionsRes.json().catch(() => ({}));

      const handle = profileData?.profile?.instagram_handle || '';
      const igConnection = Array.isArray(connectionsData?.connections)
        ? connectionsData.connections.find((c) => c.channel === 'instagram')
        : null;
      const connected = igConnection?.status === 'connected';

      this.setState({ handle, connected, loading: false });

      if (connected) {
        this.pushMessage(
          'them',
          `You\u2019re connected as @${handle || 'your account'}. Upload a reel to start scheduling chunks.`
        );
      }
    } catch {
      this.setState({ loading: false });
    }
  };

  saveProfileHandle = async (handle) => {
    try {
      const res = await fetch(urlMap.socialProfile, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          projectId: this.projectId || undefined,
          instagramHandle: handle,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP_${res.status}`);
      return true;
    } catch {
      return false;
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const answer = this.state.input.trim();
    if (!answer || this.state.saving || this.state.connected) return;

    this.pushMessage('me', answer);
    this.setState({ input: '' });

    const handle = extractInstagramHandle(answer);
    if (!handle) {
      this.pushMessage(
        'them',
        'That doesn\u2019t look like a valid Instagram handle. Try something like @yourname or paste your profile link.'
      );
      return;
    }

    this.setState({ saving: true, handle });
    const saved = await this.saveProfileHandle(handle);
    this.setState({ saving: false });

    if (!saved) {
      this.pushMessage('them', 'I couldn\u2019t save that just now. Please try again.');
      return;
    }

    this.pushMessage('them', `Great \u2014 let\u2019s connect @${handle}.`);
    this.setState({ modalOpen: true });
  };

  handleModalClose = () => {
    this.setState({ modalOpen: false });
  };

  handleConnected = () => {
    const { handle } = this.state;
    this.setState({ connected: true, modalOpen: false });
    this.pushMessage(
      'them',
      `\u2705 @${handle} is connected. Next, upload a reel and I\u2019ll help you split it into scheduled chunks.`
    );
  };

  render() {
    const { messages, input, handle, connected, loading, saving, modalOpen, isPro, proLoading } = this.state;
    const canSend = !connected && !saving && input.trim().length > 0;

    if (proLoading) {
      return <div className="social-onboarding" style={{ padding: 16 }}>Loading…</div>;
    }

    if (!isPro) {
      return (
        <div className="social-onboarding" style={{ padding: 16 }}>
          <div
            style={{
              maxWidth: 820,
              margin: '0 auto',
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: 18,
              padding: 24,
              boxShadow: '0 18px 48px rgba(0,0,0,0.12)',
            }}
          >
            <h1 style={{ margin: 0, color: 'var(--text-1)' }}>Social Media is a Pro feature</h1>
            <p style={{ marginTop: 10, color: 'var(--text-2)' }}>
              Upgrade to Pro to connect channels, draft posts, and manage publishing from your writing workflow.
            </p>
            <button type="button" className="btn primary" onClick={openSubscriptionTabInWallet}>
              View Pro subscription
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="social-onboarding" style={{ padding: 16 }}>
        <div
          style={{
            maxWidth: 820,
            margin: '0 auto',
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 18,
            overflow: 'hidden',
            boxShadow: '0 18px 48px rgba(0,0,0,0.12)',
          }}
        >
          {/* Header */}
          <div style={{ padding: 18, borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-2)' }}>
              Social Media
            </div>
            <h1 style={{ margin: '4px 0 0', fontSize: 26, lineHeight: 1.1, color: 'var(--text-1)' }}>
              Onboarding chat
            </h1>
            <div
              style={{
                marginTop: 10,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 999,
                border: '1px solid var(--border)',
                background: connected ? 'color-mix(in srgb, var(--primary) 10%, var(--card))' : 'transparent',
                color: connected ? 'var(--text-1)' : 'var(--text-2)',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: connected ? 'var(--primary)' : 'var(--text-3)',
                }}
              />
              {connected
                ? `Instagram connected${handle ? ` (@${handle})` : ''}`
                : loading
                ? 'Loading\u2026'
                : 'Not connected'}
            </div>
          </div>

          {/* Chat */}
          <div style={{ padding: 18 }}>
            <div
              className="coach-messages"
              style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 460, overflowY: 'auto', paddingRight: 6 }}
            >
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`coach-bubble ${message.from === 'me' ? 'me' : 'them'}`}
                  style={{ maxWidth: 'min(80%, 640px)' }}
                >
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{message.text}</div>
                </div>
              ))}
              <div ref={this.messagesEndRef} />
            </div>

            <form onSubmit={this.handleSubmit} style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-2)', marginBottom: 6 }}>
                  {connected ? 'Instagram connected' : 'Your Instagram handle'}
                </label>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => this.setState({ input: e.target.value })}
                  placeholder={connected ? 'Connected' : '@yourname'}
                  disabled={connected || saving}
                  style={{
                    width: '100%',
                    borderRadius: 14,
                    border: '1px solid var(--border)',
                    background: 'var(--input-bg)',
                    color: 'var(--input-text)',
                    padding: '12px 14px',
                    outline: 'none',
                    font: 'inherit',
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={!canSend}
                style={{
                  minWidth: 120,
                  height: 44,
                  border: 'none',
                  borderRadius: 12,
                  background: 'linear-gradient(180deg, var(--primary) 0%, var(--primary-hover) 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: canSend ? 'pointer' : 'not-allowed',
                  opacity: canSend ? 1 : 0.55,
                }}
              >
                {saving ? 'Saving\u2026' : 'Send'}
              </button>
            </form>

            {connected && (
              <button
                type="button"
                onClick={() => this.setState({ modalOpen: true })}
                style={{
                  marginTop: 12,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--primary)',
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Manage Instagram connection
              </button>
            )}
          </div>
        </div>

        <InstagramConnectModal
          isOpen={modalOpen}
          handle={handle}
          projectId={this.projectId}
          connected={connected}
          onClose={this.handleModalClose}
          onConnected={this.handleConnected}
        />
      </div>
    );
  }
}

export default SocialMediaPage;